import { Dropdown, Drawer } from 'flowbite';
import { fixedHeaderOffset } from './services/ui.service';

export default function theme () {

	/*
		Anchor links - smooth-scroll to the target element, offset for a
		fixed header (the header_clone() clone, or the fixed solid header -
		see fixedHeaderOffset()) so a link's target doesn't end up hidden
		underneath it. Sitewide, e.g. content-sidebar's sidebar .links and
		hero-transparent-header's '#list' button. Only href="#" (a toggle-only link, e.g. a dropdown
		trigger) is skipped - and only if the target id actually exists.
	*/
	// Two headers sit above the hero (the in-flow one and the fixed overlay).
	// After Skip to content, both menus drop out of the tab order. The next
	// stop is the hero video control, then the .book-cta links.
	const isHidden = (el) => {
		let node = el;
		while (node && node !== document.documentElement) {
			const style = getComputedStyle(node);
			if (style.display === 'none' || style.visibility === 'hidden') return true;
			node = node.parentElement;
		}
		return false;
	};

	const skipDestination = () => {
		const videoToggle = [...document.querySelectorAll('section.hero-home .video-toggle')]
			.find((el) => ! isHidden(el));
		if (videoToggle) return videoToggle;

		const bookLink = [...document.querySelectorAll('section.hero-home .book-cta a, section.hero-home .book-cta button')]
			.find((el) => ! isHidden(el));
		if (bookLink) return bookLink;

		const main = document.getElementById('main-content');
		const first = [...main.querySelectorAll('a, button, input, select, textarea')]
			.find((el) => ! isHidden(el));
		return first || main;
	};

	const muteBefore = (target) => {
		const controls = [...document.querySelectorAll('a, button, input, select, textarea')];
		const index = controls.indexOf(target);
		controls.slice(0, Math.max(index, 0)).forEach((el) => {
			if (el.classList.contains('skip-link') || el.dataset.skipMuted) return;
			el.dataset.skipMuted = '1';
			el.dataset.skipTabindex = el.getAttribute('tabindex') ?? '';
			el.tabIndex = -1;
		});
	};

	const restoreHeadersToTab = () => {
		document.querySelectorAll('[data-skip-muted]').forEach((el) => {
			if (el.dataset.skipTabindex) el.setAttribute('tabindex', el.dataset.skipTabindex);
			else el.removeAttribute('tabindex');
			delete el.dataset.skipMuted;
			delete el.dataset.skipTabindex;
		});
	};

	const skipLink = document.querySelector('.skip-link');
	let skipping = false;
	if (skipLink) {
		skipLink.addEventListener('focus', () => {
			if (skipping) return;
			restoreHeadersToTab();
		});
	}

	document.querySelectorAll('a[href^="#"]:not([href="#"])').forEach((link) => {

		link.addEventListener('click', (e) => {

			const target = document.getElementById(link.getAttribute('href').slice(1));
			if (! target) return;

			// Do not preventDefault. The browser has to follow the fragment.
			// Focus then moves to the hero video control. Both menus drop
			// out of the tab order; the booking links stay after the video.
			if (link.classList.contains('skip-link')) {
				const destination = skipDestination();
				skipping = true;
				muteBefore(destination);
				window.setTimeout(() => {
					destination.focus();
					skipping = false;
				}, 0);
				return;
			}

			e.preventDefault();

			// Target's own top padding is skipped too, so its visible content
			// always lands the same distance below the header either way
			const paddingTop = parseFloat(getComputedStyle(target).paddingTop);
			let top = target.getBoundingClientRect().top + window.scrollY + paddingTop - fixedHeaderOffset();

			// Sticky mobile header - scrolling up reveals it (see below), so an
			// upward jump has to clear it too; scrolling down hides it
			const mobileHeader = document.querySelector('.sticky-mobile-header');
			if (top < window.scrollY && mobileHeader && getComputedStyle(mobileHeader).position === 'fixed') {
				top -= mobileHeader.getBoundingClientRect().height;
			}

			window.scrollTo({
				top: top,
				behavior: 'smooth',
			});

		});

	});



	/*
		Fixed solid header - switches to the .header-2 styling (.scrolled-header)
		once the page is scrolled down a bit. See blocks/sticky-header.md.
	*/
	const solidHeader = document.querySelector('#main-header.header-solid');
	if (solidHeader) {

		const toggleScrolledHeader = () => {
			solidHeader.classList.toggle('scrolled-header', window.scrollY > 60);
		};

		toggleScrolledHeader();
		window.addEventListener('scroll', toggleScrolledHeader, { passive: true });

	}



	/*
		Sticky mobile header - slides off-screen while scrolling down, back
		while scrolling up (once the scroll in either direction passes
		DELTA), always shown within its own height of the top of the page.
		See blocks/sticky-mobile-header.md. CSS only applies below sm, so
		these classes are harmless on desktop.
	*/
	const stickyMobileHeader = document.querySelector('.sticky-mobile-header');
	if (stickyMobileHeader) {

		const DELTA = 15;
		let lastY = Math.max(0, window.scrollY);

		const toggleMobileHeader = () => {

			const y = Math.max(0, window.scrollY);

			stickyMobileHeader.classList.toggle('scrolled', y > 0);

			if (y <= stickyMobileHeader.offsetHeight) {
				stickyMobileHeader.classList.remove('off-screen');
				lastY = y;
				return;
			}

			if (Math.abs(y - lastY) < DELTA) return;

			stickyMobileHeader.classList.toggle('off-screen', y > lastY);
			lastY = y;

		};

		toggleMobileHeader();
		window.addEventListener('scroll', toggleMobileHeader, { passive: true });

	}



	/*
		Fixed mobile Book Now bar - slides in once the page is scrolled down a
		bit, see blocks/sticky-mobile-cta.md. Closes its drop-up on the way out
		so it doesn't slide down half-open.
	*/
	const stickyCta = document.querySelector('.sticky-cta');
	if (stickyCta) {

		const stickyCtaToggle = stickyCta.querySelector('.dropdown-toggle');

		const toggleScrolledCta = () => {
			const scrolled = window.scrollY > 120;
			stickyCta.classList.toggle('scrolled-cta', scrolled);
			if (! scrolled && stickyCtaToggle?.getAttribute('aria-expanded') === 'true') {
				stickyCtaToggle.click();
			}
		};

		toggleScrolledCta();
		window.addEventListener('scroll', toggleScrolledCta, { passive: true });

	}



	/*
		The sticky header clone sits before <main> and starts inert, so Skip
		to content walks the hero and then the next section. Once the hero has
		scrolled out from under it, the clone is the visible header: make that
		one active and inert the original, which has scrolled away. Below sm
		the clone is display:none and the original header stays active.
	*/
	const headerClone = document.querySelector('header.header-2');
	const headerPrimary = document.querySelector('header.header-1');
	if (headerClone && headerPrimary) {

		const hero = document.querySelector('#main-content > section');

		const syncHeaderClone = () => {

			const cloneShown = getComputedStyle(headerClone).display !== 'none';
			const heroUncovered = hero
				? hero.getBoundingClientRect().bottom <= headerClone.getBoundingClientRect().bottom + 1
				: window.scrollY > headerClone.offsetHeight;

			const cloneActive = cloneShown && heroUncovered;
			headerClone.inert = ! cloneActive;
			headerPrimary.inert = cloneActive;

		};

		syncHeaderClone();
		window.addEventListener('scroll', syncHeaderClone, { passive: true });
		window.addEventListener('resize', syncHeaderClone);

	}



	/*
		Dropdowns init - using JS API, instead of data attributes
	*/
	const dropdownToggles = document.querySelectorAll('.dropdown-toggle');
	if (dropdownToggles.length) {
		dropdownToggles.forEach(dropdownToggle => {

			const targetId = dropdownToggle.getAttribute('aria-controls');
			const targetEl = targetId
				? document.getElementById(targetId)
				: null
			;

			if (! targetEl) return;

			// Toggle-only links (href="#") shouldn't change the URL on click
			if (dropdownToggle.tagName === 'A' && dropdownToggle.getAttribute('href') === '#') {
				dropdownToggle.addEventListener('click', (e) => e.preventDefault());
			}

			// The fixed mobile .sticky-cta bar's Book Now opens upwards, its
			// bottom edge flush with the top of the bar (Figma 1092:5087)
			const isDropUp = !! dropdownToggle.closest('.sticky-cta');

			new Dropdown(targetEl, dropdownToggle, {
				placement: isDropUp ? 'top' : 'bottom',
				triggerType: 'click',
				offsetDistance: isDropUp ? 12 : 16,
				onShow: () => {
					dropdownToggle.setAttribute('aria-expanded', 'true');
				},
				onHide: () => {
					dropdownToggle.setAttribute('aria-expanded', 'false');
				},
			});

		});
	}



	/*
		Mobile Menu (drawer)
		- https://flowbite.com/docs/components/drawer/#javascript-behaviour
	*/
	// set the drawer menu element
	const drawerEl = document.getElementById('drawer');
	const menuMobile = document.getElementById('menu-mobile');

	function closeMobileSubmenus () {
		menuMobile?.querySelectorAll('.submenu.submenu-open').forEach((submenu) => {
			submenu.classList.remove('submenu-open');
			document.getElementById(submenu.getAttribute('aria-labelledby'))?.setAttribute('aria-expanded', 'false');
		});
	}

	function closeMobileBookNow () {
		menuMobile?.classList.remove('book-now-open');
		menuMobile?.querySelector('.book-now-toggle')?.setAttribute('aria-expanded', 'false');
	}

	// options with default value
	const drawerOptions = {
		placement: 'right',
		backdrop: true,
		backdropClasses: 'drawer-backdrop',
		// closed drawer is aria-hidden (Flowbite) + inert, so its links can't be tabbed to off-screen
		onShow: () => {
			drawerEl.inert = false;
		},
		onHide: () => {
			drawerEl.inert = true;
			closeMobileSubmenus();
			closeMobileBookNow();
		},
	};

	// instance options object
	const drawerInstance = {
		id: 'drawer',
	};

	const drawer = new Drawer (drawerEl, drawerOptions, drawerInstance);

	// .menu-open can appear twice (#main-header + its header_clone() clone)
	document.querySelectorAll('.menu-open').forEach((el) => {
		el.addEventListener('click', () => {
			drawer.show();
		});
	});

	document.querySelector('#drawer .menu-close').addEventListener('click', () => {
		drawer.hide();
	});

	// Toggle-only links (href="#") open a submenu/panel in place, they shouldn't close the drawer
	document.querySelectorAll('#drawer li a:not([href="#"])').forEach((el) => {
		el.addEventListener('click', () => {
			drawer.hide();
		});
	});



	/*
		Mobile Menu - General submenu (Your Visit, About)
		- slides in from the right, covering only the top-level items
	*/
	if (menuMobile) {

		menuMobile.querySelectorAll('.submenu-toggle').forEach((toggle) => {

			toggle.addEventListener('click', (e) => {

				e.preventDefault();

				const submenu = document.getElementById(toggle.getAttribute('aria-controls'));
				if (! submenu) return;

				const isOpen = submenu.classList.contains('submenu-open');

				closeMobileSubmenus();
				closeMobileBookNow();

				if (! isOpen) {
					submenu.classList.add('submenu-open');
					toggle.setAttribute('aria-expanded', 'true');
				}

			});

		});

		menuMobile.querySelectorAll('.submenu-back').forEach((backBtn) => {
			backBtn.addEventListener('click', () => closeMobileSubmenus());
		});



		/*
			Mobile Menu - "Book Now" submenu
			- expands upwards in place, covering Gift Vouchers, with a
			  semi-transparent overlay over the top-level items behind it
		*/
		const bookNowToggle = menuMobile.querySelector('.book-now-toggle');

		if (bookNowToggle) {
			bookNowToggle.addEventListener('click', (e) => {

				e.preventDefault();

				const isOpen = menuMobile.classList.contains('book-now-open');

				closeMobileSubmenus();

				menuMobile.classList.toggle('book-now-open', ! isOpen);
				bookNowToggle.setAttribute('aria-expanded', String (! isOpen));

			});
		}

	}

}
